import React, { ReactElement, ReactNode, useContext, useEffect, useRef, useState } from 'react'
import { HotelPageContext } from '@/components/hotel/HotelPageContext'
import { useTranslations } from 'next-intl'
import { useOutsideClick } from '@/lib/hooks'
import { cx } from '@/lib/utils/utils'
import DropCard from '@/components/hotel/DropCard'
import classNames from 'classnames'

export interface SelectProps {
  buttonClassName?: string
  label?: string
  options: SelectOption[]
  selectedValue: string
  className?: string
  icon?: ReactElement
  onChange: (value: string) => void
  onReset: () => void
  hideUnavailableOptions: boolean
  selectedYear?: string
}

interface SelectOption {
  value: string
  label: string
  priceChange?: number | undefined
  priceChangeAsString?: string | undefined
  action?: ReactElement | undefined
  details?: ReactElement | undefined
  openDetails?: () => void
  optionAvailable: boolean
  icon?: ReactNode
}

export function Select(props: SelectProps): ReactElement {
  const [focused, setFocused] = useState(false)
  const { modalOpen } = useContext(HotelPageContext)
  const ref = useRef<HTMLDivElement | null>(null)
  const dropdownRef = useRef<HTMLDivElement | null>(null)
  const t = useTranslations('Hotel.Controls.HotelSelect')

  useOutsideClick(
    ref,
    () => {
      if (!modalOpen) {
        setFocused(false)
      }
    },
    modalOpen,
  )

  useEffect(() => {
    if (dropdownRef.current) {
      const selectedElement = dropdownRef.current.querySelector('.selected')
      if (selectedElement) {
        selectedElement.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'nearest' })
      }
    }
  }, [selectedOption])

  function selectedOption(o: SelectOption) {
    return props.selectedValue === o.value
  }

  function selectOption(o: SelectOption) {
    props.onChange(o.value)
    setFocused(false)
  }

  function handleButtonClick(e: React.MouseEvent<HTMLButtonElement>) {
    e.preventDefault()
    setFocused(!focused)
  }

  return (
    <div ref={ref} className={cx('relative rounded-md', props.className)}>
      <button
        className={cx(
          'relative flex h-12 w-full items-center gap-2 rounded-md border border-stone-300 bg-white px-3 text-left',
          props.buttonClassName,
        )}
        onClick={handleButtonClick}
      >
        {props.icon}
      </button>
      {focused && (
        <DropCard
          additionalClassName="absolute translate-y-1 cursor-pointer bg-white w-full z-10 max-h-[250px] overflow-y-scroll"
          ref={dropdownRef}
        >
          {props.options
            .filter((o) => o.optionAvailable || props.hideUnavailableOptions == false)
            .map((o) => (
              <div
                key={o.value}
                onClick={() => selectOption(o)}
                className={classNames('flex w-full flex-col px-3 py-2', {
                  'hover:bg-stone-100': !selectedOption(o),
                  'bg-primary-lightened hover:bg-primary-lightened': selectedOption(o),
                  'opacity-50': !o.optionAvailable,
                  selected: selectedOption(o),
                })}
              >
                <div className="flex max-w-full justify-between">
                  <div className="flex items-start gap-x-3 break-all">
                    {o.icon}
                    <span className="break-normal break-words pr-2 ">{o.label}</span>
                  </div>
                  {!selectedOption(o) && (
                    <span className="flex w-20 justify-end whitespace-nowrap text-xs text-stone-500">
                      {!o.priceChange || (o.priceChange && o.priceChange < 0) ? '' : '+'}
                      {o.priceChangeAsString}
                    </span>
                  )}
                  {selectedOption(o) && (
                    <span className="flex w-20 justify-end text-xs text-primary">
                      {t('selectedOption')}
                    </span>
                  )}
                </div>
                {o.action && <div>{o.action}</div>}
              </div>
            ))}
        </DropCard>
      )}
    </div>
  )
}
